/**
 * EU AI Act Health Status Calculator
 * 
 * Automatically determines agent health based on compliance metrics
 */

export type HealthStatus = 'healthy' | 'warning' | 'critical';

export interface HealthMetrics {
  blockRate: number;
  avgConfidence: number;
  successRate: number;
  uptime: number;
}

/**
 * Calculates agent health status based on EU AI Act governance metrics
 * 
 * HEALTHY: System operating within optimal parameters
 * WARNING: Warning state - requires attention but not critical
 * CRITICAL: Immediate intervention required - compliance risk
 * 
 * @example
 * ```ts
 * const status = calculateHealthStatus({
 *   blockRate: 1.2,      // 1.2%
 *   avgConfidence: 95.2,  // 95.2%
 *   successRate: 98.9,    // 98.9%
 *   uptime: 99.8          // 99.8%
 * });
 * // Returns: 'healthy'
 * ```
 */
export function calculateHealthStatus(metrics: HealthMetrics): HealthStatus {
  // CRITICAL thresholds (EU AI Act high-risk system requirements)
  if (
    metrics.blockRate > 4.0 ||        // >4% block rate = systemic issues
    metrics.avgConfidence < 80 ||     // <80% confidence = unreliable
    metrics.successRate < 95 ||       // <95% success = compliance risk
    metrics.uptime < 97                // <97% uptime = availability issues
  ) {
    return 'critical';
  }
  
  // WARNING thresholds (early warning indicators)
  if (
    metrics.blockRate > 2.0 ||        // 2-4% block rate = degrading
    metrics.avgConfidence < 90 ||     // 80-90% confidence = needs improvement
    metrics.successRate < 98 ||       // 95-98% success = monitor closely
    metrics.uptime < 99                // 97-99% uptime = stability concerns
  ) {
    return 'warning';
  }
  
  // HEALTHY: All metrics within optimal ranges
  return 'healthy';
}

/**
 * EU AI Act Compliance Thresholds
 * 
 * These thresholds align with EU AI Act requirements for high-risk AI systems
 */
export const HEALTH_THRESHOLDS = {
  CRITICAL: {
    blockRate: 4.0,
    avgConfidence: 80,
    successRate: 95,
    uptime: 97,
  },
  WARNING: {
    blockRate: 2.0,
    avgConfidence: 90,
    successRate: 98,
    uptime: 99,
  },
  HEALTHY: {
    blockRate: 0,
    avgConfidence: 100,
    successRate: 100,
    uptime: 100,
  },
} as const;

/**
 * Get human-readable explanation for health status
 */
export function getHealthExplanation(status: HealthStatus, metrics: HealthMetrics): string {
  switch (status) {
    case 'critical':
      const criticalReasons = [];
      if (metrics.blockRate > 4.0) criticalReasons.push(`Block rate ${metrics.blockRate}% exceeds 4%`);
      if (metrics.avgConfidence < 80) criticalReasons.push(`Confidence ${metrics.avgConfidence}% below 80%`);
      if (metrics.successRate < 95) criticalReasons.push(`Success rate ${metrics.successRate}% below 95%`);
      if (metrics.uptime < 97) criticalReasons.push(`Uptime ${metrics.uptime}% below 97%`);
      return `CRITICAL: ${criticalReasons.join(', ')}. Immediate intervention required.`;
    
    case 'warning':
      const warningReasons = [];
      if (metrics.blockRate > 2.0) warningReasons.push(`Block rate ${metrics.blockRate}% above 2%`);
      if (metrics.avgConfidence < 90) warningReasons.push(`Confidence ${metrics.avgConfidence}% below 90%`);
      if (metrics.successRate < 98) warningReasons.push(`Success rate ${metrics.successRate}% below 98%`);
      if (metrics.uptime < 99) warningReasons.push(`Uptime ${metrics.uptime}% below 99%`);
      return `WARNING: ${warningReasons.join(', ')}. Monitor closely.`;
    
    case 'healthy':
      return `HEALTHY: All metrics within optimal compliance ranges.`;
  }
}